import { useReducer } from 'react';

interface WizardState {
	submittedSteps: number[];
	openedSteps: number[];
	focusedStep?: number;
	focusTick: number;
}

type Actions =
	| { type: 'initialize'; openedSteps: number[] }
	| { type: 'open-step'; index: number; focusToContent?: boolean }
	| { type: 'close-step'; index: number }
	| { type: 'open-next-step'; index: number }
	| { type: 'reset' }
	| { type: 'submit-step'; index: number };

const reducer = (state: WizardState, action: Actions): WizardState => {
	switch (action.type) {
		case 'initialize':
			return {
				...state,
				openedSteps: action.openedSteps,
			};
		case 'open-step':
			return {
				...state,
				openedSteps: [...state.openedSteps, action.index],
				focusedStep: action.focusToContent ? action.index : state.focusedStep,
				focusTick: action.focusToContent
					? state.focusTick + 1
					: state.focusTick,
			};
		case 'close-step':
			return {
				...state,
				openedSteps: state.openedSteps.filter((step) => step !== action.index),
			};
		case 'open-next-step':
			return {
				...state,
				openedSteps: [action.index],
				focusedStep: action.index,
				focusTick: state.focusTick + 1,
			};
		case 'reset':
			return {
				...state,
				openedSteps: [0],
				focusedStep: 0,
				submittedSteps: [],
				focusTick: state.focusTick + 1,
			};
		case 'submit-step':
			return {
				...state,
				submittedSteps: [...state.submittedSteps, action.index],
			};
		default:
			return state;
	}
};

export const useWizardReducer = () => {
	const [state, dispatch] = useReducer(reducer, {
		submittedSteps: [],
		openedSteps: [],
		focusTick: 0,
	});

	return {
		...state,
		openStep: (index: number, focusToContent = true) =>
			dispatch({ type: 'open-step', index, focusToContent }),
		initialize: (openedSteps: number[]) =>
			dispatch({ type: 'initialize', openedSteps }),
		closeStep: (index: number) => dispatch({ type: 'close-step', index }),
		openNextStep: (index: number) =>
			dispatch({ type: 'open-next-step', index }),
		reset: () => dispatch({ type: 'reset' }),
		submitStep: (index: number) => dispatch({ type: 'submit-step', index }),
	};
};
